import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronRight, UserPlus, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import type { ChampionshipDetail, ChampionshipTeamRow, PaginatedResult } from "@/types";
import { personName, teamLabel } from "@/lib/championships";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { SortHeader, nextSort, type SortState } from "@/components/ui/SortHeader";
import { TeamFormModal } from "@/components/championships/TeamFormModal";

type SortKey = "pareja" | "jugador1" | "jugador2" | "partidos";

// Parejas inscritas: listado paginado en servidor, con búsqueda, orden y Excel. Pinchar una fila
// abre la ficha de la pareja, donde se edita.
export function TeamsSection({ campeonato }: { campeonato: ChampionshipDetail }) {
  const navigate = useNavigate();
  const id = campeonato.id;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [sort, setSort] = useState<SortState<SortKey>>({ key: "pareja", dir: "asc" });
  const [creating, setCreating] = useState(false);

  const filterParams = useMemo(() => {
    const params = new URLSearchParams({ sort: sort.key, dir: sort.dir });
    if (search) params.set("search", search);
    return params;
  }, [search, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ["championship", id, "equipos", "list", page, limit, filterParams.toString()],
    queryFn: async () => {
      const params = new URLSearchParams(filterParams);
      params.set("page", String(page));
      params.set("limit", String(limit));
      return (await api.get<PaginatedResult<ChampionshipTeamRow>>(`/championships/${id}/equipos?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  function toggleSort(key: SortKey) {
    setSort((s) => nextSort(s, key));
    setPage(1);
  }

  const rows = data?.data ?? [];

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
          <Users2 className="h-4 w-4 text-brand-600" />
          Parejas inscritas ({campeonato.equiposCount})
        </p>
        {campeonato.canManage && (
          <Button onClick={() => setCreating(true)}>
            <UserPlus className="h-4 w-4" />
            Inscribir pareja
          </Button>
        )}
      </div>

      {campeonato.equiposCount > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="col-span-2 sm:col-span-1">
              <Input
                label="Buscar"
                placeholder="Pareja o jugador…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {searchInput ? (
              <Button
                onClick={() => {
                  setSearchInput("");
                  setPage(1);
                }}
              >
                Limpiar filtros
              </Button>
            ) : (
              <span />
            )}
            <ExportExcelButton url={`/championships/${id}/equipos/export.xlsx?${filterParams.toString()}`} filename="parejas-campeonato.xlsx" />
          </div>
        </>
      )}

      {isLoading ? (
        <TableSkeleton rows={3} cols={4} />
      ) : !rows.length ? (
        <p className="text-sm text-ink-400">
          {search ? "Ninguna pareja coincide con la búsqueda." : "Todavía no hay ninguna pareja inscrita."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
              <tr>
                <th className="px-3 py-3 sm:px-4">
                  <SortHeader label="Pareja" active={sort.key === "pareja"} dir={sort.dir} onClick={() => toggleSort("pareja")} />
                </th>
                <th className="hidden px-4 py-3 xl:table-cell xl:w-[24%]">
                  <SortHeader label="Jugador 1" active={sort.key === "jugador1"} dir={sort.dir} onClick={() => toggleSort("jugador1")} />
                </th>
                <th className="hidden px-4 py-3 xl:table-cell xl:w-[24%]">
                  <SortHeader label="Jugador 2" active={sort.key === "jugador2"} dir={sort.dir} onClick={() => toggleSort("jugador2")} />
                </th>
                <th className="hidden px-4 py-3 sm:table-cell sm:w-28">
                  <SortHeader label="Partidos" active={sort.key === "partidos"} dir={sort.dir} onClick={() => toggleSort("partidos")} />
                </th>
                <th className="w-9 px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => navigate(`/campeonatos/${id}/equipos/${e.id}`)}
                  className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50/50"
                >
                  <td className="px-3 py-3 sm:px-4">
                    <p className="line-clamp-2 font-medium break-words text-ink-800 sm:line-clamp-1" title={teamLabel(e)}>
                      {teamLabel(e)}
                    </p>
                    {/* Jugadores y nº de partidos tienen columna propia solo en pantallas anchas */}
                    <p className="truncate text-xs text-ink-400">
                      {e.nombre && <span className="xl:hidden">{personName(e.jugador1)} / {personName(e.jugador2)} · </span>}
                      <span className="sm:hidden">{e.partidosCount} partido{e.partidosCount === 1 ? "" : "s"}</span>
                    </p>
                  </td>
                  <td className="hidden truncate px-4 py-3 text-ink-500 xl:table-cell" title={personName(e.jugador1)}>
                    {personName(e.jugador1)}
                  </td>
                  <td className="hidden truncate px-4 py-3 text-ink-500 xl:table-cell" title={personName(e.jugador2)}>
                    {personName(e.jugador2)}
                  </td>
                  <td className="hidden px-4 py-3 text-ink-500 sm:table-cell">{e.partidosCount}</td>
                  <td className="px-2 py-3 text-ink-300">
                    <ChevronRight className="h-4 w-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={data?.page ?? 1}
        totalPages={data?.totalPages ?? 1}
        total={data?.total ?? 0}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      {creating && <TeamFormModal championshipId={id} onOpenChange={(open) => !open && setCreating(false)} />}
    </Card>
  );
}
